import { Box, TabPanels, Tabs, useToast } from '@chakra-ui/react';
import { useAuthContext } from 'contexts/auth';
import { motion } from 'framer-motion';
import { ContractSearchRequestDTO, ContractSearchResponseDTO } from 'models';
import { useCallback, useState } from 'react';
import ContractService from 'services/contracts';
import { removeEmptyValues } from 'utils/removeEmptyValues';
import { FilterContract } from './FilterContracts/Filter';
import { FilesTab } from './Tabs/FilesTab';
import { HistoryTab } from './Tabs/HistoryTab';
import { SearchResultTab } from './Tabs/SearchResultTab';
import { TabCustom, TabListCustom, TabPanelCustom } from './styles';

export const SearchContracts = () => {
  const [contracts, setContracts] = useState<ContractSearchResponseDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastFilters, setLastFilters] = useState<ContractSearchRequestDTO>({});
  const [hasSearched, setHasSearched] = useState(false);
  const { isBackoffice } = useAuthContext();
  const toast = useToast()
  const [tabIndex, setTabIndex] = useState(0);

  const performSearch = useCallback(
    async (filters: ContractSearchRequestDTO, page = 1, limit = 10) => {
      if (Object.keys(filters).length === 0 && !hasSearched) {
        return;
      }

      setIsLoading(true);
      setIsError(false);
      try {
        const searchParams = { ...filters, page, limit };
        const response = await ContractService.getContractsByQuery(searchParams);
        const { data, headers } = response;
        const newTotalPages = headers['x-total-pages'] ? Number(headers['x-total-pages']) : 1;

        setContracts(data);
        setTotalPages(newTotalPages);
        setCurrentPage(page);
        setLastFilters(filters);
      } catch (error: any) {
        toast({
            title: 'Erro ao buscar o contrato no Detran!',
            description: error?.response?.data[0]?.mensagem || 'Ocorreu um erro inesperado.',
            status: 'error',
            isClosable: true        
        });

        setIsError(true);
        setContracts([]);
        setTotalPages(0);
      } finally {
        setIsLoading(false);
      }
    },
    [toast] 
  );

  const handleSearch = (data: ContractSearchRequestDTO) => {
    if (!hasSearched) setHasSearched(true);
      const dataToSubmit = { ...data };
      if (dataToSubmit.cnpjAgenteFinanceiro) {
        dataToSubmit.cnpjAgenteFinanceiro = dataToSubmit.cnpjAgenteFinanceiro.replace(/\D/g, '');
      }
      if (dataToSubmit.cpfCnpjDevedor) {
        dataToSubmit.cpfCnpjDevedor = dataToSubmit.cpfCnpjDevedor.replace(/\D/g, '')
      }      
      const filteredData = removeEmptyValues(dataToSubmit);
      performSearch(filteredData, 1);
  };

  const handlePaginate = (page: number, limit: number) => {
    if (hasSearched) {
      performSearch(lastFilters, page, limit);
    }
  };

  return (
    <Box>
      <Tabs
        isLazy
        colorScheme="primary"
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}       
        onChange={(index) => setTabIndex(index)}
        mt={4}
      >
        <TabListCustom>
          <TabCustom>Consulta de Contratos</TabCustom>
          <TabCustom>Consulta de Arquivos</TabCustom>
          <TabCustom>Histórico de Consultas</TabCustom>
        </TabListCustom>

        <TabPanels>
          <TabPanelCustom>
            <FilterContract onSearch={handleSearch} isLoading={isLoading} isBackoffice={isBackoffice} />
            {hasSearched && (
              <SearchResultTab
                contracts={contracts}
                isLoading={isLoading}
                isError={isError}
                totalPages={totalPages}
                currentPage={currentPage}
                onPaginate={handlePaginate}
              />
            )}
          </TabPanelCustom>
          <TabPanelCustom>
                          <FilesTab isBackoffice={isBackoffice} />
          </TabPanelCustom>

          <TabPanelCustom>
            {/* Passamos o tabIndex para a HistoryTab */}
            <HistoryTab activeTabIndex={tabIndex} />
          </TabPanelCustom>
        </TabPanels>
      </Tabs>
    </Box>
  );
};